var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var Form_1;
import { injectable } from '@slickdeals/durango/dist/javascript/decorators/injectable';
import { showable } from '@slickdeals/durango/dist/javascript/decorators/showable';
import { stateable } from '@slickdeals/durango/dist/javascript/decorators/stateable';
import { classNameWithModifier } from '@slickdeals/durango/dist/javascript/utilities/css';
import Logger from '@slickdeals/durango/dist/javascript/utilities/logger';
import BlueprintComponent from '../core/blueprintComponent';
import { validatable } from '../decorators/validatable';
import { EvaluationTypes } from '../validation/validatorService';
import '../../scss/components/form.scss';
/** The name of the module */
export const name = 'Form';
export var ClassNames;
(function (ClassNames) {
    ClassNames["Default"] = "bp-c-form";
    ClassNames["JavaScript"] = "js-form";
})(ClassNames || (ClassNames = {}));
/**
 * @deprecated Use ClassNames enum instead
 *
 * The base CSS classname for the main element controlled by this module
 */
export const CSS_CLASSNAME = 'bp-c-form';
/**
 * @deprecated Use ClassNames enum instead
 *
 * Class name used for error message styling
 */
const ERROR_CONTAINER_CLASS_NAME = 'bp-c-form_errorMessages';
/**
 * @deprecated Use ClassNames enum instead
 *
 * Class name used for error message styling
 */
const ERROR_CLASS_NAME = 'bp-c-form_errorMessage';
/**
 * @deprecated Use ClassNames enum instead
 *
 * Class name used for error styles on form
 */
const FORM_ERROR_CLASS_NAME = classNameWithModifier(CSS_CLASSNAME, 'error');
export const FormSubmissionEvents = {
    Success: `${name}:submission:success`,
    Failure: `${name}:submission:failure`,
};
export var FormSubmissionTypes;
(function (FormSubmissionTypes) {
    FormSubmissionTypes["Default"] = "default";
    FormSubmissionTypes["Fetch"] = "fetch";
})(FormSubmissionTypes || (FormSubmissionTypes = {}));
export var EagerTextEvaluationTypes;
(function (EagerTextEvaluationTypes) {
    EagerTextEvaluationTypes["TextArea"] = "textarea";
    EagerTextEvaluationTypes["Text"] = "text";
})(EagerTextEvaluationTypes || (EagerTextEvaluationTypes = {}));
export const isEagerTextEvaluationType = (value) => Object.values(EagerTextEvaluationTypes).includes(value);
let Form = Form_1 = class Form extends BlueprintComponent {
    constructor(options) {
        super(name, options);
        // Initialize the state
        const initState = {
            isSubmitButtonEnabled: false,
            visible: this.options.visible ?? true,
        };
        this.endpoint = this.options.endpoint;
        this.formSubmissionType = this.options.formSubmissionType ?? FormSubmissionTypes.Default;
        // Initialize the module's state
        this.initState(initState);
    }
    /**
       * Create a new instance of this element
       * @param options The options to hydrate this component with
       */
    static create(options) {
        return new Form_1(options);
    }
    static createElement(content) {
        const element = document.createElement('form');
        element.appendChild(content);
        return element;
    }
    async init() {
        super.init();
        this.element = await BlueprintComponent.loadElement(name, this.options);
        // Gather our validation rules (via component options passed in via twig) and set them up for evaluation
        this.setValidationRules(this.options.validations);
        this.element.addEventListener('input', this.onInput.bind(this));
        this.element.addEventListener('submit', this.handleSubmit.bind(this));
        // Bind our submit button to the form
        this.submitButton = this.element.querySelector('button[type=submit]');
        // By default, disable submit button
        if (this.submitButton) {
            this.submitButton.disabled = true;
        }
    }
    /**
       * Handles eager validation on user form input. Eager being, validate on user input.
       * @param event user input event
       */
    async onInput(event) {
        // If not a supported text evaluation type, don't evaluate since it will break validation
        if (!isEagerTextEvaluationType(event.target.type)) {
            return;
        }
        const evaluationErrors = await this.validateEvaluationRules(EvaluationTypes.eager, event);
        this.handleEvaluationErrors(evaluationErrors);
    }
    /**
       * Handles form submission. Lazy validation rules are handled here. Lazy being, validation on submit.
       * @param event - user submit event
       */
    async handleSubmit(event) {
        event.preventDefault();
        const evaluationErrors = await this.validateEvaluationRules();
        this.handleEvaluationErrors(evaluationErrors);
        if (this.getState().isSubmitButtonEnabled) {
            this.setState({
                isSubmitButtonEnabled: false,
            });
            // Disable the submit button state so users can't spam the submit button
            // and submit the form multiple times
            this.setSubmitButtonState();
            if (this.formSubmissionType === FormSubmissionTypes.Fetch) {
                Logger.log('Form submitted to server using Fetch');
                await this.submit(new FormData(this.element));
                this.setState({
                    isSubmitButtonEnabled: true,
                });
                // Re-enable the submit button for another form submission attempt
                this.setSubmitButtonState();
            }
            else {
                Logger.log('Form submitted to server using form submit');
                this.element.submit();
            }
        }
    }
    /**
     * Updates component styles and state based on existence of errors
     *
     * @param EvaluationErrors a Map of validation errors or true if there are no errors
     */
    async handleEvaluationErrors(evaluationErrors) {
        if (evaluationErrors === true) {
            this.element.classList.remove(FORM_ERROR_CLASS_NAME);
            this.setState({
                isSubmitButtonEnabled: true,
            });
            this.clearErrorMessages();
        }
        else {
            this.setState({
                isSubmitButtonEnabled: false,
            });
            this.element.classList.add(FORM_ERROR_CLASS_NAME);
            this.displayErrorMessage(evaluationErrors);
        }
        this.setSubmitButtonState();
    }
    /**
       * Clears error messages inside of error messages container if they exist
       */
    clearErrorMessages() {
        if (this.errorContainer) {
            this.removeChildren(this.errorContainer);
        }
    }
    /**
       * Loops through direct children of parent and removes them from DOM
       *
       * @param parent parent container containing children
       */
    removeChildren(parent) {
        while (parent.firstChild) {
            parent.removeChild(parent.firstChild);
        }
    }
    /**
       * Creates and displays error message
       */
    displayErrorMessage(evaluationErrors) {
        // If error container doesn't exist, create it.
        if (!this.errorContainer) {
            this.errorContainer = document.createElement('div');
            this.errorContainer.classList.add(ERROR_CONTAINER_CLASS_NAME);
            this.errorContainer.dataset.qaFormErrorMessages = '';
            // Append created container right after form element as DOM sibling
            this.element.insertAdjacentElement('afterend', this.errorContainer);
            this.errorContainer = this.element.nextSibling;
        }
        this.clearErrorMessages();
        // Iterate through errors array and append them to errors container
        evaluationErrors.forEach(moduleErrors => {
            moduleErrors.forEach(error => {
                // create a paragraph element
                const paragraph = document.createElement('p');
                // create error message text
                const errorMessage = document.createTextNode(error.message);
                // add error message text to paragraph
                paragraph.appendChild(errorMessage);
                // add class name to error for styling
                paragraph.classList.add(ERROR_CLASS_NAME);
                // add data-attribute for easy identification
                paragraph.dataset.qaFormErrorMessage = error.message;
                // add paragraph containing error message to error container
                this.errorContainer.appendChild(paragraph);
            });
        });
    }
    /**
     * Creates error message elements in the same fashion as internal validation errors.
     */
    static createErrorMessage(message) {
        const errorContainer = document.createElement('div');
        errorContainer.classList.add(ERROR_CONTAINER_CLASS_NAME);
        const error = document.createElement('p');
        error.classList.add(ERROR_CLASS_NAME);
        error.textContent = message;
        errorContainer.appendChild(error);
        return errorContainer;
    }
    /**
       * Disables/enables submit button based on form's current state
       */
    setSubmitButtonState() {
        if (this.submitButton) {
            this.submitButton.disabled = !this.getState().isSubmitButtonEnabled;
        }
    }
    /**
       * Submits the form data to server
       * @param formData
       * @returns {boolean} - Whether the form submission was successful or not
       */
    submit(formData) {
        return fetch(this.endpoint, {
            method: 'post',
            body: formData,
        })
            .then(response => response.json())
            .then(data => {
            Logger.log('Success:', data);
            this.element.dispatchEvent(new CustomEvent(FormSubmissionEvents.Success, {
                detail: data,
            }));
        })
            .catch(error => {
            Logger.error('Error:', error);
            this.element.dispatchEvent(new CustomEvent(FormSubmissionEvents.Failure, {
                detail: error,
            }));
        });
    }
};
Form = Form_1 = __decorate([
    stateable(name),
    validatable,
    showable(),
    injectable(name)
], Form);
export default Form;
BlueprintComponent.instantiate(name);
